/**
 * The Workbook: the sheet index, the tab order, and the address-resolving façade
 * the rest of the kernel talks to.
 *
 * Every mutating entry point here validates before it mutates. Sheet creation
 * checks the 10-sheet limit and the name rules first, cell writes check the
 * 5,000,000-row and 16,384-column bounds first, and a bulk write checks the
 * shape of the whole payload first. A rejected operation therefore leaves the
 * Workbook byte-for-byte unchanged, which is what Requirement 16.1 and
 * Properties 2 and 3 ask for.
 *
 * A Workbook always holds at least one sheet: deleting the last remaining sheet
 * is rejected, so the sheet count stays inside `[1, 10]` (Property 3).
 *
 * Addresses and ranges carry their own {@link SheetId}, so the functions below
 * resolve the sheet from the address rather than taking it separately. An
 * unknown id raises {@link UnknownSheetError} instead of silently creating a
 * sheet, because a mistyped id in a command is a bug, not an intent.
 */

import { LimitError, SheetNameError, UnknownSheetError } from '../errors.js';
import {
  MAX_SHEETS,
  normalizeRange,
  type Addr,
  type CellValue,
  type Direction,
  type Range,
  type ScalarType,
  type SheetId,
} from '../types.js';
import type { FormulaCell } from './chunk.js';
import {
  blockEdge as sheetBlockEdge,
  cellStyleId as sheetCellStyleId,
  clearFormula as sheetClearFormula,
  clearValues as sheetClearValues,
  colWidth as sheetColWidth,
  columnType as sheetColumnType,
  createSheetData,
  declareColumnType as sheetDeclareColumnType,
  deleteCols as sheetDeleteCols,
  deleteRows as sheetDeleteRows,
  formulaCells as sheetFormulaCells,
  freezePane as sheetFreezePane,
  getCell as sheetGetCell,
  getFormula as sheetGetFormula,
  insertCols as sheetInsertCols,
  insertRows as sheetInsertRows,
  rowHeight as sheetRowHeight,
  setCell as sheetSetCell,
  setCellStyle as sheetSetCellStyle,
  setColWidth as sheetSetColWidth,
  setFormula as sheetSetFormula,
  setRegion as sheetSetRegion,
  setRowHeight as sheetSetRowHeight,
  sheetMemory,
  tileOf,
  usedRange as sheetUsedRange,
  writeSheetData as sheetWriteData,
  type Sheet,
  type SheetData,
  type SheetMeta,
  type StoreMemory,
  type TileSnapshot,
} from './sheet.js';

/** Longest sheet name accepted, matching Excel's tab-name limit. */
export const MAX_SHEET_NAME_LENGTH = 31;

/** Name given to the sheet a fresh Workbook starts with. */
export const DEFAULT_SHEET_NAME = 'Sheet1';

export interface Workbook {
  id: string;
  name: string;
  /** Save-format version; bumped only by the Persistence_Service. */
  version: 1;
  /** Sheet data, keyed by stable {@link SheetId}. */
  sheets: Map<SheetId, Sheet>;
  /** Tab order. Holds exactly the keys of {@link Workbook.sheets}. */
  order: SheetId[];
  /** The sheet the Grid_Renderer is currently showing. */
  activeSheetId: SheetId;
  /** Monotonic counter behind generated sheet ids; never reused. */
  nextSheetSeq: number;
}

export interface CreateWorkbookOptions {
  id?: string;
  name?: string;
  /** Name of the initial sheet. Defaults to {@link DEFAULT_SHEET_NAME}. */
  firstSheetName?: string;
}

export function createWorkbook(opts: CreateWorkbookOptions = {}): Workbook {
  const wb: Workbook = {
    id: opts.id ?? 'wb-1',
    name: opts.name ?? 'Untitled workbook',
    version: 1,
    sheets: new Map(),
    order: [],
    activeSheetId: '',
    nextSheetSeq: 0,
  };
  const first = createSheet(wb, opts.firstSheetName ?? DEFAULT_SHEET_NAME);
  wb.activeSheetId = first;
  return wb;
}

/* ------------------------------------------------------------------ sheets */

/** Number of sheets held. Always inside `[1, 10]` after construction. */
export function sheetCount(wb: Workbook): number {
  return wb.order.length;
}

export function hasSheet(wb: Workbook, id: SheetId): boolean {
  const targetId = id === '' ? (wb.order[0] ?? 'sheet-1') : id;
  return wb.sheets.has(targetId);
}

/** Resolves a sheet id. Throws {@link UnknownSheetError} when it is not held. */
export function getSheet(wb: Workbook, id: SheetId): Sheet {
  const targetId = id === '' ? (wb.order[0] ?? 'sheet-1') : id;
  const sheet = wb.sheets.get(targetId);
  if (sheet === undefined) throw new UnknownSheetError(id);
  return sheet;
}

/** Sheet metadata in tab order, with `index` refreshed from that order. */
export function listSheets(wb: Workbook): SheetMeta[] {
  return wb.order.map((id, index) => {
    const meta = getSheet(wb, id).meta;
    meta.index = index;
    return meta;
  });
}

/** Id of the sheet whose name matches `name`, case-insensitively. */
export function sheetIdByName(wb: Workbook, name: string): SheetId | null {
  const folded = name.toLowerCase();
  for (const id of wb.order) {
    if (getSheet(wb, id).meta.name.toLowerCase() === folded) return id;
  }
  return null;
}

function checkSheetName(wb: Workbook, name: string, exceptId?: SheetId): void {
  if (name.trim().length === 0) throw new SheetNameError(name, 'name is blank');
  if (name.length > MAX_SHEET_NAME_LENGTH) {
    throw new SheetNameError(name, `name exceeds ${MAX_SHEET_NAME_LENGTH} characters`);
  }
  if (name.includes('!')) throw new SheetNameError(name, "name may not contain '!'");
  const existing = sheetIdByName(wb, name);
  if (existing !== null && existing !== exceptId) {
    throw new SheetNameError(name, 'name is already used by another sheet');
  }
}

/**
 * `Sheet1`, `Sheet2`, … skipping names already in use. Used when the caller
 * supplies no name, so the UI's "add sheet" button never fails on a collision.
 */
export function nextAvailableSheetName(wb: Workbook): string {
  for (let n = wb.order.length + 1; ; n++) {
    const candidate = `Sheet${n}`;
    if (sheetIdByName(wb, candidate) === null) return candidate;
  }
}

/**
 * Adds a sheet and returns its id.
 *
 * Rejects the 11th sheet with a {@link LimitError} carrying both the limit and
 * the requested count, and leaves the Workbook unchanged (Requirements 1.3,
 * 1.11, 16.1).
 */
export function createSheet(wb: Workbook, name?: string): SheetId {
  const count = wb.order.length;
  if (count >= MAX_SHEETS) {
    throw new LimitError(MAX_SHEETS, count + 1, 'sheets', 'sheet limit reached');
  }
  const chosen = name ?? nextAvailableSheetName(wb);
  checkSheetName(wb, chosen);

  const id: SheetId = `sheet-${++wb.nextSheetSeq}`;
  wb.sheets.set(id, createSheetData(id, chosen, count));
  wb.order.push(id);
  if (wb.activeSheetId === '') wb.activeSheetId = id;
  return id;
}

/**
 * Removes a sheet and its data. Deleting the last remaining sheet is rejected,
 * so the count invariant of Property 3 holds. When the active sheet goes away,
 * the neighbouring tab becomes active.
 */
export function deleteSheet(wb: Workbook, id: SheetId): void {
  const position = wb.order.indexOf(id);
  if (position === -1) throw new UnknownSheetError(id);
  if (wb.order.length === 1) {
    throw new LimitError(1, 0, 'sheets', 'a workbook must keep at least one sheet');
  }
  wb.order.splice(position, 1);
  wb.sheets.delete(id);
  reindex(wb);
  if (wb.activeSheetId === id) {
    wb.activeSheetId = wb.order[Math.min(position, wb.order.length - 1)] as SheetId;
  }
}

export function renameSheet(wb: Workbook, id: SheetId, name: string): void {
  const sheet = getSheet(wb, id);
  checkSheetName(wb, name, id);
  sheet.meta.name = name;
}

/** Moves a tab to `toIndex`, clamped into range. */
export function moveSheet(wb: Workbook, id: SheetId, toIndex: number): void {
  const position = wb.order.indexOf(id);
  if (position === -1) throw new UnknownSheetError(id);
  if (!Number.isInteger(toIndex)) throw new RangeError(`tab index must be an integer: ${toIndex}`);
  const target = Math.min(Math.max(toIndex, 0), wb.order.length - 1);
  wb.order.splice(position, 1);
  wb.order.splice(target, 0, id);
  reindex(wb);
}

export function activeSheet(wb: Workbook): Sheet {
  return getSheet(wb, wb.activeSheetId);
}

export function activateSheet(wb: Workbook, id: SheetId): void {
  getSheet(wb, id);
  wb.activeSheetId = id;
}

/** Activates the next (`+1`) or previous (`-1`) tab, wrapping around. */
export function activateAdjacentSheet(wb: Workbook, step: 1 | -1): SheetId {
  const position = wb.order.indexOf(wb.activeSheetId);
  const count = wb.order.length;
  const next = wb.order[(position + step + count) % count] as SheetId;
  wb.activeSheetId = next;
  return next;
}

function reindex(wb: Workbook): void {
  wb.order.forEach((id, index) => {
    const sheet = wb.sheets.get(id);
    if (sheet !== undefined) sheet.meta.index = index;
  });
}

/* ------------------------------------------------------------------ values */

export function getCell(wb: Workbook, at: Addr): CellValue {
  return sheetGetCell(getSheet(wb, at.sheet), at.row, at.col);
}

export function setCell(wb: Workbook, at: Addr, value: CellValue): void {
  sheetSetCell(getSheet(wb, at.sheet), at.row, at.col, value);
}

/** Bulk write. Shape and bounds are checked before the first cell is touched. */
export function setRegion(wb: Workbook, range: Range, values: readonly CellValue[][]): void {
  sheetSetRegion(getSheet(wb, range.sheet), range, values);
}

/** Clears values across `range`, retaining formatting (Requirement 2.8). */
export function clearValues(wb: Workbook, range: Range): void {
  sheetClearValues(getSheet(wb, range.sheet), range);
}

/** Flat, transferable snapshot of a rectangular region (Requirement 1.4). */
export function getTile(wb: Workbook, range: Range): TileSnapshot {
  return tileOf(getSheet(wb, range.sheet), range);
}

/* ---------------------------------------------------------------- formulas */

/** The formula record at `at`, or `undefined` when the cell holds a literal. */
export function getFormula(wb: Workbook, at: Addr): FormulaCell | undefined {
  return sheetGetFormula(getSheet(wb, at.sheet), at.row, at.col);
}

/** Stores a formula source, its cached syntax tree, and its computed value. */
export function setFormula(wb: Workbook, at: Addr, cell: FormulaCell): void {
  sheetSetFormula(getSheet(wb, at.sheet), at.row, at.col, cell);
}

/** Drops the formula record at `at`, leaving its last computed value behind. */
export function clearFormula(wb: Workbook, at: Addr): void {
  sheetClearFormula(getSheet(wb, at.sheet), at.row, at.col);
}

/** Every formula cell on one sheet. */
export function formulaCells(wb: Workbook, sheet: SheetId): Addr[] {
  return sheetFormulaCells(getSheet(wb, sheet));
}

/** Every formula cell in the workbook, in tab order. */
export function allFormulaCells(wb: Workbook): Addr[] {
  const out: Addr[] = [];
  for (const id of wb.order) out.push(...sheetFormulaCells(getSheet(wb, id)));
  return out;
}

export function columnType(wb: Workbook, sheet: SheetId, col: number): ScalarType {
  return sheetColumnType(getSheet(wb, sheet), col);
}

/** Asserts a column type inferred on import (Requirement 10.7). */
export function declareColumnType(
  wb: Workbook,
  sheet: SheetId,
  col: number,
  type: ScalarType,
): void {
  sheetDeclareColumnType(getSheet(wb, sheet), col, type);
}

/**
 * Replaces a sheet's cell data wholesale and returns what was there
 * (Requirement 10.2).
 *
 * The Import_Service's commit step: a staged table swaps in, the target's name,
 * tab position, geometry, and freeze stay put, and the previous data comes back as
 * the value the inverse patch keeps.
 */
export function writeSheetData(wb: Workbook, sheet: SheetId, data: SheetData): SheetData {
  return sheetWriteData(getSheet(wb, sheet), data);
}

export function cellStyleId(wb: Workbook, at: Addr): number {
  return sheetCellStyleId(getSheet(wb, at.sheet), at.row, at.col);
}

export function setCellStyle(wb: Workbook, at: Addr, styleId: number): void {
  sheetSetCellStyle(getSheet(wb, at.sheet), at.row, at.col, styleId);
}

/* ------------------------------------------------------- structural edits */

/**
 * Inserts `count` blank rows at `at` (Requirement 4.13).
 *
 * Values, formulas, styles, row heights, merged regions, and the freeze origin
 * all move together. An insertion that would push a non-empty cell past row
 * 5,000,000 is rejected with a {@link LimitError} and changes nothing.
 */
export function insertRows(wb: Workbook, sheet: SheetId, at: number, count: number): void {
  sheetInsertRows(getSheet(wb, sheet), at, count);
}

/** Deletes the `count` rows at `at`, pulling everything below them up. */
export function deleteRows(wb: Workbook, sheet: SheetId, at: number, count: number): void {
  sheetDeleteRows(getSheet(wb, sheet), at, count);
}

/** Inserts `count` blank columns at `at` (Requirement 4.13). */
export function insertCols(wb: Workbook, sheet: SheetId, at: number, count: number): void {
  sheetInsertCols(getSheet(wb, sheet), at, count);
}

/** Deletes the `count` columns at `at`, pulling everything to their right left. */
export function deleteCols(wb: Workbook, sheet: SheetId, at: number, count: number): void {
  sheetDeleteCols(getSheet(wb, sheet), at, count);
}

/* -------------------------------------------------------------- navigation */

/** Bounding box of the non-empty cells (Requirement 3.5, `Ctrl+End`). */
export function usedRange(wb: Workbook, sheet: SheetId): Range {
  return sheetUsedRange(getSheet(wb, sheet));
}

/** `Ctrl+End` target: the corner of the used range (Requirement 3.5). */
export function lastUsedCell(wb: Workbook, sheet: SheetId): Addr {
  const r = usedRange(wb, sheet);
  return { sheet, row: r.r1, col: r.c1 };
}

/** `Ctrl+Arrow` target (Requirements 3.2, 3.3). */
export function blockEdge(wb: Workbook, from: Addr, dir: Direction): Addr {
  return sheetBlockEdge(getSheet(wb, from.sheet), from.row, from.col, dir);
}

/* ---------------------------------------------------------------- geometry */

export function colWidth(wb: Workbook, sheet: SheetId, col: number): number {
  return sheetColWidth(getSheet(wb, sheet), col);
}

export function rowHeight(wb: Workbook, sheet: SheetId, row: number): number {
  return sheetRowHeight(getSheet(wb, sheet), row);
}

export function setColWidth(wb: Workbook, sheet: SheetId, col: number, px: number): void {
  sheetSetColWidth(getSheet(wb, sheet), col, px);
}

export function setRowHeight(wb: Workbook, sheet: SheetId, row: number, px: number): void {
  sheetSetRowHeight(getSheet(wb, sheet), row, px);
}

/** Sets or clears the freeze origin (Requirement 1.8). */
export function freezePane(wb: Workbook, sheet: SheetId, at: Addr | null): void {
  sheetFreezePane(getSheet(wb, sheet), at);
}

export function freezeOf(wb: Workbook, sheet: SheetId): Addr | null {
  return getSheet(wb, sheet).meta.freeze;
}

/* ------------------------------------------------------------------ merges */

/** Merged region covering `at`, or `null` (Requirement 6.8 support). */
export function mergeAt(wb: Workbook, at: Addr): Range | null {
  for (const r of getSheet(wb, at.sheet).meta.merges) {
    const n = normalizeRange(r);
    if (at.row >= n.r0 && at.row <= n.r1 && at.col >= n.c0 && at.col <= n.c1) return n;
  }
  return null;
}

/* ------------------------------------------------------------------ memory */

/** Per-sheet and total memory accounting (Requirement 14.6, Property 59). */
export function workbookMemory(wb: Workbook): StoreMemory {
  const total: StoreMemory = {
    chunkCount: 0,
    allocatedCells: 0,
    cellBytes: 0,
    overheadBytes: 0,
    sparseBytes: 0,
    dictBytes: 0,
    totalBytes: 0,
  };
  for (const sheet of wb.sheets.values()) {
    const m = sheetMemory(sheet);
    total.chunkCount += m.chunkCount;
    total.allocatedCells += m.allocatedCells;
    total.cellBytes += m.cellBytes;
    total.overheadBytes += m.overheadBytes;
    total.sparseBytes += m.sparseBytes;
    total.dictBytes += m.dictBytes;
    total.totalBytes += m.totalBytes;
  }
  return total;
}

/** Resident bytes attributable to stored cell data (Requirement 14.6 guard). */
export function memoryBytes(wb: Workbook): number {
  return workbookMemory(wb).totalBytes;
}
